import type { PowerLevel, SessionState } from './scene';
import { AnimatePresence, motion } from 'framer-motion';
export interface HUDProps {
  state: SessionState;
  onPause: () => void;
  onPower: (power: PowerLevel) => void;
}
export function HUD({ state, onPause, onPower }: HUDProps) {
  const remainingSeconds = Math.max(0, Math.ceil(state.timeRemaining));
  const timeLabel = `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-20 px-3 pt-[max(1rem,env(safe-area-inset-top))] text-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-[72px] shrink-0 rounded-2xl border border-white/10 bg-slate-950/55 px-3 py-2 shadow-lg backdrop-blur-md">
          <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">Coins</div>
          <div className="text-xl font-black tabular-nums text-amber-300">{state.coins}</div>
        </div>
        <div className="min-w-[76px] shrink-0 rounded-2xl border border-white/10 bg-slate-950/55 px-3 py-2 text-center shadow-lg backdrop-blur-md">
          <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">Score</div>
          <div className="text-xl font-black tabular-nums text-amber-300">{state.score}</div>
        </div>
        <div className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-slate-950/55 px-3 py-2 text-center shadow-lg backdrop-blur-md">
          <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">Time</div>
          <div className="whitespace-nowrap text-lg font-black tabular-nums">{timeLabel}</div>
        </div>
        <button
          type="button"
          className="pointer-events-auto h-12 w-12 shrink-0 rounded-2xl border border-white/10 bg-slate-950/55 text-xl font-black backdrop-blur-md transition hover:bg-slate-950/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300 active:scale-90"
          aria-label={state.paused ? 'Resume game' : 'Pause game'}
          onClick={onPause}
        >
          {state.paused ? '▶' : 'Ⅱ'}
        </button>
      </div>
      <div className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-2xl border border-white/10 bg-slate-950/55 p-2 shadow-lg backdrop-blur-md">
        <span className="px-1 text-[10px] font-bold uppercase tracking-widest text-white/75">Power</span>
        {([1, 2, 3] as const).map((level) => {
          const selected = state.power === level;
          const affordable = state.coins >= POWER_COSTS[level];
          return (
            <button
              key={level}
              type="button"
              aria-pressed={selected}
              onClick={() => onPower(level)}
              className={`pointer-events-auto h-12 min-w-12 rounded-xl border px-3 text-sm font-black transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300 active:scale-90 ${selected ? 'border-amber-300 bg-amber-300 text-slate-950' : affordable ? 'border-white/15 bg-white/10 text-white hover:bg-white/20' : 'border-red-400 bg-red-500/20 text-red-200'}`}
            >
              {level} · {POWER_COSTS[level]}¢
            </button>
          );
        })}
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={state.paused ? 'paused' : state.cannonCanFire ? 'ready' : 'empty'}
          className={`motion-framer mx-auto mt-2 w-fit rounded-full border px-3 py-1 text-xs font-bold ${state.paused ? 'border-cyan-300/50 bg-cyan-950/80 text-cyan-100' : state.cannonCanFire ? 'border-cyan-300/30 bg-cyan-950/60 text-cyan-100' : 'border-red-400/40 bg-red-950/65 text-red-100'}`}
          initial={{ opacity: 0, scale: 0.82, y: -5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.82, y: -5 }}
          transition={{ type: 'spring', stiffness: 420, damping: 30, mass: 0.7 }}
          aria-live="polite"
        >
          {state.paused
            ? 'Session paused'
            : state.cannonCanFire
              ? 'Cannon ready'
              : 'Not enough coins'}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
const POWER_COSTS = { 1: 1, 2: 3, 3: 5 } as const;
